import { useState } from 'react'
import { track } from '@/lib/analytics'
import { useLocale } from '@/i18n/locale'
import { NavLink } from '@/components/NavLink'
import './value-calculator.css'

const LABEL = 'font-mono text-small uppercase tracking-wider'
const NBSP = '\u00a0'

/**
 * ValueCalculator — интерактивный расчёт годового эффекта, 2 режима.
 * Философия (КП Тони): «не обещаем абстрактных процентов, считаем на ваших
 * цифрах» — формула + вилка «от-до». Дисциплина вилок ~1:2.
 * hydration-safe: локаль стабильна per-SSG-страница, дефолты детерминированы.
 */
type Mode = 'enterprise' | 'core'
type Plan = 'save' | 'delegate'

/* Между числом и единицей — неразрывный пробел: «11,6 млн ₽» в крупной
   цифре карточки не должно ломаться посреди суммы. Десятые держим до
   сотни миллионов: «11,6 млн», а не «12 млн» — строка контекста считается
   из вводных пользователя, и округление там читается как подгонка. */
function fmtMoney(value: number, isEn: boolean): string {
  if (isEn) {
    if (value >= 1_000_000) {
      const m = value / 1_000_000
      const r = m >= 100 ? Math.round(m) : Math.round(m * 10) / 10
      return `$${r}M`
    }
    if (value >= 1000) return `$${Math.round(value / 1000)}K`
    return `$${Math.round(value)}`
  }
  if (value >= 1_000_000) {
    const m = value / 1_000_000
    const r = m >= 100 ? Math.round(m) : Math.round(m * 10) / 10
    return `${String(r).replace('.', ',')}${NBSP}млн${NBSP}₽`
  }
  if (value >= 1000) return `${Math.round(value / 1000)}${NBSP}тыс${NBSP}₽`
  return `${Math.round(value)}${NBSP}₽`
}

/* Часы и проценты по правилам локали: «3 870 ч», «15,9 %» — в русском
   разряды через пробел, дробь через запятую и пробел перед знаком процента;
   в английском «3,870 h», «15.9%». */
function fmtHours(h: number, isEn: boolean): string {
  return `${Math.round(h).toLocaleString(isEn ? 'en-US' : 'ru-RU')}${NBSP}${isEn ? 'h' : 'ч'}`
}
function fmtPct(p: number, isEn: boolean): string {
  const n = p.toLocaleString(isEn ? 'en-US' : 'ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return isEn ? `${n}%` : `${n}${NBSP}%`
}

function NumberField({
  label, suffix, value, min, max, step, locale, onChange,
}: {
  label: string; suffix?: string; value: number; min: number; max: number
  step: number; locale: string; onChange: (v: number) => void
}) {
  return (
    <label className="block">
      <span className="text-small text-muted">{label}</span>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-current"
          style={{ accentColor: 'rgb(var(--accent-rgb))' }}
        />
        <span className="font-mono text-body text-ink font-medium tabular-nums whitespace-nowrap min-w-[5.5rem] text-right">
          {value.toLocaleString(locale)}
          {suffix ? <span className="text-muted text-small"> {suffix}</span> : null}
        </span>
      </div>
    </label>
  )
}

const COPY = {
  ru: {
    kicker: 'ROI-калькулятор внедрения ИИ',
    heading: 'Мы не рисуем ROI. Мы считаем на ваших цифрах.',
    // Два предложения, не четыре (аудит 2026-09-21: вводки-пересказы).
    // Рамка «цель — не уволить максимум» (мандат Тони) из абзаца ушла, но
    // держится механикой: план по умолчанию — «делегировать», главная цифра
    // — часы в работу, а не экономия на людях; оговорка «сокращать штат —
    // только после работающей автоматизации» стоит под кнопкой.
    lede: 'Конкуренты обещают «сэкономите 40%». Мы ваших цифр не знаем и выдумывать не будем: прикиньте эффект внедрения ИИ сами, а точно посчитаем на бесплатной диагностике.',
    // Вкладки — одним словом: на телефоне «Внедрение в команду» и
    // «ИИ-ядро холдинга» ломались на две строки, а второе ещё и не
    // расшифровывалось. Что считается — объясняют подписи ползунков.
    tabA: 'Команда', tabB: 'Холдинг',
    aF1: 'Руководителей, включая вас',
    aF2: 'Часов в неделю на рутине у каждого', aF2s: 'ч',
    aF3: 'Стоимость часа руководителя', aF3s: '₽',
    planLabel: 'Как распорядиться высвобожденным временем',
    planDelegate: 'Делегировать больше задач тем же людям',
    planDelegateNote: 'Ставка на объём: те же люди с ИИ-помощниками делают больше и лучше.',
    planSave: 'Заменить часть команды и сэкономить на ФОТ',
    planSaveNote: 'Ставка на издержки: освободившиеся часы перестают оплачиваться.',
    // Строка контекста над результатом — из вводных самого пользователя.
    // Она же несёт оговорку про «половину», которая раньше стояла отдельным
    // абзацем под ползунками.
    ctx1: 'Сейчас рутина руководителей — ', ctx2: ' в год, это ',
    ctx3: ' уже оплаченного времени (по вашим цифрам). Считаем, что машина закроет половину — ',
    ctx4: ': консервативно и не обещание.',
    perYear: 'в год',
    resDelegate: 'Возвращается в бизнес',
    resDelegateSub: (pct: string) => `в задачи, которые двигают бизнес · +${pct} мощности команды`,
    growthNote: 'В деньгах не оцениваем: это маржа ваших задач.',
    evenIf1: 'Даже если считать только часы, которые перестанут оплачиваться, — ',
    // Цены на сайте нет (Тони 2026-09-27) — окупаемость от неё не показываем:
    // считаем её на бесплатной диагностике под ваш состав.
    evenIf2: ' в год. Окупаемость посчитаем на бесплатной диагностике — под ваш состав.',
    resFot: 'Экономия ФОТ в год',
    resSaveSub: 'только часы, которые перестанут оплачиваться',
    saveNote: 'Окупаемость посчитаем на бесплатной диагностике — под ваш состав.',
    saveGrowth: (h: string, pct: string) => `Ещё ${h} в год уходит в задачи, которые двигают бизнес (+${pct} мощности команды) — в деньгах не оцениваем.`,
    bF1: 'Средний остаток на счетах', bF1s: 'млн ₽',
    bF2: 'Доля простаивает без размещения', bF2s: '%',
    bF3: 'Ставка размещения свободных остатков', bF3s: '%',
    bFormula: 'Формула: остаток × доля простоя × ставка. Это только доход от ликвидности — сверху ещё FX-потери, ручной труд и межкомпанийные комиссии.',
    resB: 'Высвобожденный доход',
    noteB: 'Только по свободной ликвидности. С учётом FX, ручного труда и межкомпанийных расчётов эффект выше — считаем детально на пилоте.',
    cta: 'Записаться на диагностику',
    disclaimer: 'Расчёт — ориентир по порядку величины, не оферта. Сокращать штат — только после работающей автоматизации, не до.',
  },
  en: {
    kicker: 'AI implementation ROI calculator',
    heading: 'We don’t paint ROI. We count on your numbers.',
    lede: 'Competitors promise “you’ll save 40%.” We don’t know your numbers and we won’t invent them: estimate the effect of an AI rollout yourself, and on the free diagnostics call we’ll count it precisely.',
    tabA: 'Team', tabB: 'Holding',
    aF1: 'Executives, including you',
    aF2: 'Hours a week each spends on routine', aF2s: 'h',
    aF3: 'Cost of an executive hour', aF3s: '$',
    planLabel: 'What to do with the freed-up time',
    planDelegate: 'Delegate more work to the same people',
    planDelegateNote: 'Betting on volume: the same people with AI assistants do more, and better.',
    planSave: 'Replace part of the team and cut payroll',
    planSaveNote: 'Betting on cost: the freed hours stop being paid for.',
    ctx1: 'Right now your executives’ routine is ', ctx2: ' a year — ',
    ctx3: ' of already-paid time (by your numbers). We assume a machine will cover half — ',
    ctx4: ': conservative, and not a promise.',
    perYear: 'a year',
    resDelegate: 'Back into the business',
    resDelegateSub: (pct: string) => `into work that moves the business · +${pct} team capacity`,
    growthNote: 'We don’t price it: that is the margin of your own tasks.',
    evenIf1: 'Even counting only the hours that stop being paid for — ',
    evenIf2: ' a year. We’ll calculate the payback at the free diagnostics — for your scope.',
    resFot: 'Payroll saved per year',
    resSaveSub: 'only the hours that stop being paid for',
    saveNote: 'We’ll calculate the payback at the free diagnostics — for your scope.',
    saveGrowth: (h: string, pct: string) => `Another ${h} a year go into work that moves the business (+${pct} team capacity) — not priced.`,
    bF1: 'Average balance across accounts', bF1s: '$M',
    bF2: 'Share idle, not placed', bF2s: '%',
    bF3: 'Placement rate on free balances', bF3s: '%',
    bFormula: 'Formula: balance × idle share × rate. This is liquidity income only — on top of it come FX losses, manual labor and intercompany fees.',
    resB: 'Freed-up income',
    noteB: 'Free liquidity only. With FX, manual labor and intercompany settlements the effect is higher — we model it in detail on the pilot.',
    cta: 'Book AI diagnostics',
    disclaimer: 'An order-of-magnitude estimate, not an offer. Cut headcount only after an automation works, not before.',
  },
}

export function ValueCalculator() {
  const isEn = useLocale() === 'en'
  const c = isEn ? COPY.en : COPY.ru
  const numLocale = isEn ? 'en-US' : 'ru-RU'
  const [mode, setMode] = useState<Mode>('enterprise')

  // Дефолты локале-зависимы (₽ vs $), но стабильны per-SSG-страница.
  const [leaders, setLeaders] = useState(5)
  const [hoursPerWeek, setHoursPerWeek] = useState(15)
  const [hourCost, setHourCost] = useState(isEn ? 40 : 3000)

  const [balanceM, setBalanceM] = useState(isEn ? 5 : 50)
  const [idlePct, setIdlePct] = useState(30)
  const [ratePct, setRatePct] = useState(isEn ? 8 : 10)

  // Раньше здесь стояли два процентных ползунка — «доля рутины, которую
  // закроет машина» и «сколько уходит в рост». Ни один не читался:
  // собственник не мыслит процентами автоматизации, он мыслит решением
  // «кого-то заменить» или «нагрузить сильных». Вопрос переформулирован
  // в выбор из двух, а проценты стали его следствием.
  const [plan, setPlan] = useState<Plan>('delegate')

  // Доля рутины, которую машина закрывает не хуже человека. Консервативные
  // 50% — и это НЕ обещание: точную цифру даёт диагностика на живых
  // процессах, о чём сказано рядом.
  const AUTO_SHARE = 0.5
  // Выбор определяет, куда идёт освобождённое время. Не 100/0: даже при
  // ставке на экономию часть времени уходит в работу, и наоборот.
  const toGrowth = plan === 'delegate' ? 0.85 : 0.25

  // Часы рутины в год по всей команде в контуре — и сколько они уже стоят
  // компании по ставке, которую назвал собственник. Это не эффект, а
  // контекст: оплаченное время, из которого дальше считается всё остальное.
  const routineHoursYear = leaders * hoursPerWeek * 4.3 * 12
  const routineCostYear = routineHoursYear * hourCost
  // Из них машина забирает столько, сколько указал собственник
  const freedHours = routineHoursYear * AUTO_SHARE

  // Путь 1 — экономия ФОТ. Освобождённые часы, которые НЕ нужно оплачивать.
  // Считается в деньгах прямо: часы × ставка.
  const savedHours = freedHours * (1 - toGrowth)
  const fotSaving = savedHours * hourCost

  // Путь 2 — рост исполнения. Те же часы уходят в задачи, которые двигают
  // бизнес. В ДЕНЬГАХ мы их не оцениваем: их ценность равна марже этих
  // задач, а её знает только собственник. Показываем в мощности команды.
  const growthHours = freedHours * toGrowth
  const capacityGainPct = routineHoursYear > 0 ? (growthHours / (leaders * 40 * 4.3 * 12)) * 100 : 0


  const liquidityYear = balanceM * 1_000_000 * (idlePct / 100) * (ratePct / 100)
  const bLow = liquidityYear
  const bHigh = liquidityYear * 1.4

  const isA = mode === 'enterprise'

  return (
    /* Единственная секция страницы, которую потолок в 896px не отпускал ни
       на 1920, ни на 2560: калькулятор занимал меньше половины экрана,
       пока всё вокруг шло во всю ширину. Теперь он живёт в общем
       контейнере, а комфортную длину строки держат сами колонки. */
    <section className="section bg-card" id="calculator">
      <div className="container-x">
        <div className="max-w-3xl">
          <p className={`${LABEL} text-muted mb-5`}>{c.kicker}</p>
          <h2 className="text-h2 text-ink">{c.heading}</h2>
          <p className="mt-5 text-body text-muted leading-relaxed">{c.lede}</p>
        </div>

        <div className="mt-8 inline-flex rounded-btn border border-divider p-1 bg-bg">
          <button
            type="button"
            onClick={() => { setMode('enterprise'); track('calc_mode_switch', { mode: 'enterprise' }) }}
            className={`px-4 py-2 rounded-btn text-small font-medium transition-colors ${isA ? 'bg-ink text-bg' : 'text-muted hover:text-ink'}`}
          >
            {c.tabA}
          </button>
          <button
            type="button"
            onClick={() => { setMode('core'); track('calc_mode_switch', { mode: 'core' }) }}
            className={`px-4 py-2 rounded-btn text-small font-medium transition-colors ${!isA ? 'bg-ink text-bg' : 'text-muted hover:text-ink'}`}
          >
            {c.tabB}
          </button>
        </div>

        {/* Порядок в потоке — ползунки → результат → выбор плана: на телефоне
            движение ползунка сразу отзывается цифрой, а не упирается в два
            крупных переключателя. На десктопе сетка раскладывает явно:
            ползунки и план — левая колонка в две строки, карточка — правая
            на обе строки. Вторая строка — 1fr: иначе высота карточки
            делится между строками, и план отъезжает от ползунков. */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 lg:grid-rows-[auto_1fr] gap-5 lg:gap-8 xl:gap-12 items-start">
          <div className="lg:col-span-7 space-y-5 lg:space-y-6">
            {isA ? (
              <>
                <NumberField label={c.aF1} value={leaders} min={1} max={20} step={1} locale={numLocale} onChange={setLeaders} />
                <NumberField label={c.aF2} suffix={c.aF2s} value={hoursPerWeek} min={2} max={40} step={1} locale={numLocale} onChange={setHoursPerWeek} />
                <NumberField label={c.aF3} suffix={c.aF3s} value={hourCost} min={isEn ? 10 : 500} max={isEn ? 200 : 10000} step={isEn ? 5 : 500} locale={numLocale} onChange={setHourCost} />
              </>
            ) : (
              <>
                <NumberField label={c.bF1} suffix={c.bF1s} value={balanceM} min={isEn ? 1 : 5} max={isEn ? 200 : 500} step={isEn ? 1 : 5} locale={numLocale} onChange={setBalanceM} />
                <NumberField label={c.bF2} suffix={c.bF2s} value={idlePct} min={5} max={70} step={5} locale={numLocale} onChange={setIdlePct} />
                <NumberField label={c.bF3} suffix={c.bF3s} value={ratePct} min={3} max={25} step={1} locale={numLocale} onChange={setRatePct} />
                <p className="text-small text-muted leading-snug">{c.bFormula}</p>
              </>
            )}
          </div>

          <div className={`lg:col-start-8 lg:col-span-5 lg:row-start-1 ${isA ? 'lg:row-span-2' : ''} lg:sticky lg:top-24 rounded-card border border-divider p-5 sm:p-8 xl:p-10 bg-bg`}>
            {isA ? (
              <>
                {/* Контекст из вводных: сколько рутина уже стоит компании.
                    Это самая крупная честная цифра на тех же данных, и без
                    неё результат ниже читался как «эффект копеечный». */}
                <p className="text-small text-muted leading-snug">
                  {c.ctx1}<strong className="font-medium text-ink tabular-nums">{fmtHours(routineHoursYear, isEn)}</strong>
                  {c.ctx2}<strong className="font-medium text-ink tabular-nums">{fmtMoney(routineCostYear, isEn)}</strong>
                  {c.ctx3}<strong className="font-medium text-ink tabular-nums">{fmtHours(freedHours, isEn)}</strong>
                  {c.ctx4}
                </p>

                {/* Главная цифра — по выбранному плану. Раньше при
                    «делегировать» крупно стояла экономия ФОТ — 15 %
                    высвобожденных часов, которые план как раз не экономит, —
                    и подпись спорила с выбором. Формулы те же, поменялось
                    только то, что показано первым. */}
                <div className="mt-4 pt-4 border-t border-divider">
                  {plan === 'delegate' ? (
                    <>
                      <div className={`${LABEL} text-muted mb-3`}>{c.resDelegate}</div>
                      <div className="calc-figure font-mono font-semibold text-ink tabular-nums">
                        {fmtHours(growthHours, isEn)}
                        <span className="calc-figure-unit text-muted font-medium">{NBSP}{c.perYear}</span>
                      </div>
                      <p className="mt-3 text-body text-ink leading-snug">{c.resDelegateSub(fmtPct(capacityGainPct, isEn))}</p>
                      <p className="mt-2 text-small text-muted leading-snug">{c.growthNote}</p>
                      <p className="mt-4 pt-4 border-t border-divider text-small text-muted leading-snug">
                        {c.evenIf1}<span className="text-ink tabular-nums">{fmtMoney(fotSaving, isEn)}</span>
                        {c.evenIf2}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className={`${LABEL} text-muted mb-3`}>{c.resFot}</div>
                      <div className="calc-figure font-mono font-semibold text-ink tabular-nums">{fmtMoney(fotSaving, isEn)}</div>
                      <p className="mt-3 text-body text-ink leading-snug">{c.resSaveSub}</p>
                      <p className="mt-2 text-small text-muted leading-snug">{c.saveNote}</p>
                      <p className="mt-4 pt-4 border-t border-divider text-small text-muted leading-snug">
                        {c.saveGrowth(fmtHours(growthHours, isEn), fmtPct(capacityGainPct, isEn))}
                      </p>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={`${LABEL} text-muted mb-3`}>{c.resB} · {c.perYear}</div>
                <div className="calc-figure font-mono font-semibold text-ink tabular-nums">
                  {fmtMoney(bLow, isEn)}
                  <span className="text-muted"> – </span>
                  {fmtMoney(bHigh, isEn)}
                </div>
                <p className="mt-4 text-small text-muted leading-snug">{c.noteB}</p>
              </>
            )}
            {/* Цена стоит только в режиме А, внутри строки об окупаемости: во
                втором режиме считается доход от ликвидности, и сопоставлять
                его со стоимостью внедрения семантически некорректно.
                Множителя нет намеренно — на верхних положениях слайдеров он
                давал бы «177×», а это ровно та инфоцыганщина, от которой мы
                отстраиваемся. Цифры рядом, вывод делает собственник. */}
            <NavLink
              href="#final-cta"
              className="btn-cta no-underline text-center mt-5 w-full"
              onClick={() => track('calc_cta_click', { mode })}
            >
              {c.cta}
            </NavLink>
            <p className="mt-4 text-small text-muted leading-snug">{c.disclaimer}</p>
          </div>

          {isA && (
            <fieldset className="lg:col-span-7 lg:col-start-1 lg:row-start-2">
              <legend className="text-small text-muted mb-2.5">{c.planLabel}</legend>
              <div className="space-y-2.5">
                {([
                  ['delegate', c.planDelegate, c.planDelegateNote],
                  ['save', c.planSave, c.planSaveNote],
                ] as const).map(([key, title, note]) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={plan === key}
                    onClick={() => { setPlan(key); track('calc_plan_switch', { plan: key }) }}
                    className={`block w-full text-left rounded-card border p-4 transition-colors ${
                      plan === key ? 'border-ink bg-bg' : 'border-divider hover:border-muted'
                    }`}
                  >
                    <span className="flex gap-3">
                      <span
                        aria-hidden
                        className={`mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-2 ${
                          plan === key ? 'border-accent bg-accent' : 'border-divider'
                        }`}
                      />
                      <span>
                        <span className="block text-body text-ink leading-snug">{title}</span>
                        {/* На телефоне пояснение — только у выбранного плана:
                            два раскрытых описания стоили половину экрана. */}
                        <span className={`mt-1 text-small text-muted leading-snug ${plan === key ? 'block' : 'hidden lg:block'}`}>{note}</span>
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </fieldset>
          )}
        </div>
      </div>
    </section>
  )
}
